/**
 * DataTable primitive (interface layer).
 *
 * Locked TypeScript signature from the design doc. Wraps TanStack Table v8
 * for column rendering + cursor/offset pagination + sort, plus the agreed
 * empty/loading/error states.
 *
 * Pagination model:
 *   - 'offset' → uses defaultPageSize + tracks offset locally; emits
 *     { offset, limit } to fetcher.
 *   - 'cursor' → keeps a stack of cursors; emits { cursor, limit }. The
 *     fetcher signals end-of-data by omitting nextCursor.
 */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface DataTableColumn<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => ReactNode);
  sortable?: boolean;
  filterable?: boolean;
  width?: number | 'auto';
}

export interface DataTableFetchParams {
  cursor?: string;
  offset?: number;
  limit: number;
  sort?: { id: string; desc: boolean };
  filters?: Record<string, string | number | boolean>;
}

export interface DataTablePage<T> {
  rows: T[];
  total?: number;
  nextCursor?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  fetcher: (params: DataTableFetchParams) => Promise<DataTablePage<T>>;
  pagination: 'cursor' | 'offset';
  defaultPageSize: number;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyState?: ReactNode;
  errorState?: (err: Error, retry: () => void) => ReactNode;
  filters?: Record<string, string | number | boolean>;
}

type LoadState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'ready'; page: DataTablePage<T> };

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    fetcher,
    pagination,
    defaultPageSize,
    rowKey,
    onRowClick,
    emptyState,
    errorState,
    filters,
  } = props;

  const [state, setState] = useState<LoadState<T>>({ kind: 'loading' });
  const [offset, setOffset] = useState(0);
  // For cursor mode, we track a stack so Back works.
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);

  const currentCursor = cursorStack[cursorStack.length - 1];

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const params: DataTableFetchParams = {
        limit: defaultPageSize,
        filters,
      };
      if (pagination === 'offset') {
        params.offset = offset;
      } else {
        if (currentCursor !== undefined) params.cursor = currentCursor;
      }
      const page = await fetcher(params);
      setState({ kind: 'ready', page });
    } catch (e) {
      setState({ kind: 'error', error: e as Error });
    }
  }, [fetcher, pagination, defaultPageSize, offset, currentCursor, filters]);

  useEffect(() => {
    // Debounce: collapses keystroke storms (search input firing on every char)
    // AND React's render-induced fetcher-reference changes into a single request.
    // QA-2026-04-25: was firing 2× per mount + 1× per keystroke.
    const timer = setTimeout(() => { load(); }, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const tanstackColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((c) => ({
        id: c.id,
        header: c.header,
        cell: ({ row }) => {
          const r = row.original;
          if (typeof c.accessor === 'function') return c.accessor(r);
          return r[c.accessor] as ReactNode;
        },
      })),
    [columns],
  );

  const rows = state.kind === 'ready' ? state.page.rows : [];

  const table = useReactTable<T>({
    data: rows,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: rowKey,
  });

  const handleNext = () => {
    if (pagination === 'offset') {
      setOffset((o) => o + defaultPageSize);
    } else {
      const next = state.kind === 'ready' ? state.page.nextCursor : undefined;
      if (next) setCursorStack((s) => [...s, next]);
    }
  };

  const handlePrev = () => {
    if (pagination === 'offset') {
      setOffset((o) => Math.max(0, o - defaultPageSize));
    } else {
      setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
    }
  };

  const canNext =
    state.kind === 'ready' &&
    (pagination === 'cursor'
      ? Boolean(state.page.nextCursor)
      : (state.page.total ?? 0) > offset + defaultPageSize);
  const canPrev =
    pagination === 'cursor' ? cursorStack.length > 1 : offset > 0;

  if (state.kind === 'loading') {
    return (
      <div data-testid="datatable-loading" className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (state.kind === 'error') {
    if (errorState) return <>{errorState(state.error, load)}</>;
    return (
      <div data-testid="datatable-error" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {state.error.message}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        {emptyState ?? <span data-testid="datatable-empty">No results.</span>}
      </div>
    );
  }

  return (
    <div className="datatable overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {/* Wide tables scroll inside this box; the page header and pager stay put */}
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500"
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {table.getRowModel().rows.map((r) => (
            <tr
              key={r.id}
              onClick={onRowClick ? () => onRowClick(r.original) : undefined}
              className={onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}
            >
              {r.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-4 py-2 text-sm text-slate-900">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 text-sm text-slate-600">
        <span>
          {pagination === 'offset' && state.page.total !== undefined
            ? `${offset + 1}–${Math.min(offset + rows.length, state.page.total)} of ${state.page.total}`
            : `${rows.length} rows`}
        </span>
        <span className="space-x-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={!canPrev}
            className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canNext}
            className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </span>
      </div>
    </div>
  );
}
